import { BadgeActionCard, LoadingActionCard } from '@account/components';
import { EarnedBadge } from '@account/types';
import { PaginationReturn } from '@common/types/service';
import { CenteredEmptyState } from '@components/display/emptyStates';
import { useMaxWidthWrapperScrollElement } from '@components/layout/MaxWidthWrapper';
import { isInfiniteLoadingItem, useInfiniteLazyLoader } from '@hooks/useInfiniteLazyLoader';
import { Grid2, useMediaQuery } from '@mui/material';
import { useLazyGetMyEarnedBadgesQuery } from '@services/accounts';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

export const EarnedBadgesDesktop = () => {
    //#region Hooks
    const isMdOrAbove = useMediaQuery((theme: any) => theme.breakpoints.up('md'));
    const isLg = useMediaQuery((theme) => theme.breakpoints.up('lg'));
    const isMd = useMediaQuery((theme) => theme.breakpoints.up('md'));
    const isSm = useMediaQuery((theme) => theme.breakpoints.up('sm'));
    const desktopPageSize = isLg ? 16 : isMd ? 12 : isSm ? 8 : 4;
    const scrollElement = useMaxWidthWrapperScrollElement();
    //#endregion

    //#region Data fetching
    const [loadEarnedBadgesTrigger, { isError: isEarnedBadgesDesktopError }] = useLazyGetMyEarnedBadgesQuery();

    const loadEarnedBadgesPage = useCallback(
        async ({ page, pageSize }: { page: number; pageSize: number }) => {
            const response = await loadEarnedBadgesTrigger(
                {
                    page,
                    page_size: pageSize,
                },
                true
            ).unwrap();

            return response as PaginationReturn<EarnedBadge>;
        },
        [loadEarnedBadgesTrigger]
    );

    const {
        items: earnedBadgeDesktopItems,
        sentinelRef,
        hasRealItems,
        isLoadingMore,
    } = useInfiniteLazyLoader<EarnedBadge>({
        pageSize: desktopPageSize,
        scrollElement,
        contextKey: `earned-badges:${desktopPageSize}`,
        loadPage: loadEarnedBadgesPage,
        getItemKey: (badge) => badge.id,
        onError: (error) => {
            console.error('Error loading earned badges:', error);
        },
        enabled: isMdOrAbove,
    });

    return (
        <Grid2 container pb="24px" spacing="24px">
            {hasRealItems || isLoadingMore ? (
                earnedBadgeDesktopItems.map((badge, index) =>
                    isInfiniteLoadingItem(badge) ? (
                        <Grid2 key={`skeleton-${index}`} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                            <LoadingActionCard />
                        </Grid2>
                    ) : (
                        <Grid2 key={`badge-${badge.id}`} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                            <BadgeActionCard badge={badge} className="!min-w-0 !w-auto" />
                        </Grid2>
                    )
                )
            ) : isEarnedBadgesDesktopError ? (
                <CenteredEmptyState
                    message={
                        <FormattedMessage
                            id="account.events.error"
                            defaultMessage="An error occurred while fetching your earned badges."
                        />
                    }
                />
            ) : (
                <CenteredEmptyState
                    message={
                        <FormattedMessage
                            id="account.events.noEarnedBadges"
                            defaultMessage="You haven't earned any badges yet."
                        />
                    }
                />
            )}
            <div ref={sentinelRef} className="basis-full h-px" />
        </Grid2>
    );
};
